import { useMemo } from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import { TextArea } from '../../Form/TextArea'
import { AccessSelect } from './AccessSelect'
import { useUserContext } from '../../../../../context/UserProvider'
import { Input } from '../../../../cars/components/V3/_shared/Form/Input'
import { MultiSelectNew } from '../../Form/MultiSelectNew'

type AccessValueType = 'YES' | 'NO' | 'OWN_CAR_ONLY'

export const MessageForm = () => {
  const { chosenCompany } = useUserContext()
  const { control, setValue } = useFormContext()

  const message = useWatch({ control, name: 'webUser.message' }) ?? ''
  const selectedCodes = useWatch({ control, name: 'webUser.filterAccess' }) ?? []
  const access = useWatch({ control, name: 'webUser.access' }) ?? 'YES'
  const requiresRegNo = access === 'OWN_CAR_ONLY'

  const consolidatedAgreements = useMemo(() => {
    if (!chosenCompany?.consolidateAgreement) return [{ value: '_ALL_', label: 'Velg alle', isChecked: false }]

    const base = chosenCompany.consolidateAgreement.map((item) => ({
      value: item.ConsolidateCode,
      label: `${item.ConsolidateCode} - ${item.ConsolidateDescription}`,
      isChecked: selectedCodes.includes(item.ConsolidateCode),
    }))

    return [{ value: '_ALL_', label: 'Velg alle', isChecked: selectedCodes.length === base.length }, ...base]
  }, [chosenCompany?.consolidateAgreement, selectedCodes])

  const toggleSelection = (value: string) => {
    if (!chosenCompany?.consolidateAgreement) return

    if (value === '_ALL_') {
      const allCodes = chosenCompany.consolidateAgreement.map((x) => x.ConsolidateCode)
      const next = selectedCodes.length === allCodes.length ? [] : allCodes
      setValue('webUser.filterAccess', next, { shouldDirty: true })
      return
    }

    const next = selectedCodes.includes(value)
      ? selectedCodes.filter((v: string) => v !== value)
      : [...selectedCodes, value]

    setValue('webUser.filterAccess', next, { shouldDirty: true })
  }

  if (!chosenCompany) return null
  const MAX_MESSAGE_LENGTH = 200

  return (
    <div className="flex flex-col gap-5">
      <div className="flex gap-4 flex-col sm:flex-row sm:gap-2">
        <div className="w-full">
          <div className="font-semibold text-[14px]">Tilgang til hele selskapet</div>
          <AccessSelect
            name="webUser.access"
            value={access as AccessValueType}
            onValueChange={(value) => setValue('webUser.access', value, { shouldDirty: true })}
          />
        </div>

        <div className="w-full">
          <MultiSelectNew
            id="webUserFilterAccess"
            label="Velg samlefakturafilter"
            placeholder="Velg samlefakturafilter"
            options={consolidatedAgreements}
            handleChange={toggleSelection}
          />
        </div>
      </div>

      {requiresRegNo && (
        <div className="flex gap-4 flex-col sm:flex-row sm:gap-2">
          <div className="w-full">
            <Controller
              control={control}
              name="webUser.regNo"
              render={({ field }) => (
                <Input
                  id="webRegNo"
                  placeholder="Reg nummer"
                  label="Reg nummer *"
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  required
                />
              )}
            />
          </div>
        </div>
      )}

      <div>
        <TextArea
          id="webUsermessage"
          label="Melding *"
          placeholder="Skriv din melding her..."
          rows={5}
          value={message}
          onChange={(e) => setValue('webUser.message', e.target.value, { shouldDirty: true, shouldValidate: true })}
          maxLength={MAX_MESSAGE_LENGTH}
          isError={message.length >= MAX_MESSAGE_LENGTH}
          error="Du har brukt opp maks antall tegn"
          required
        />
        <span>
          {message.length}/{MAX_MESSAGE_LENGTH}
        </span>
      </div>
    </div>
  )
}
